import React, { useState, useRef } from 'react';
import api from '../utils/api';

interface ColumnMapping {
  column: string;
  gear_type: string;
  classification: 'frontline' | 'backup' | null;
  matched_item_type: boolean;
}

interface PreviewItem {
  gearType: string;
  classification: 'frontline' | 'backup' | null;
  dateManufactured: string | null;
  instanceNumber: number;
  sourceColumn: string;
}

interface PreviewRow {
  rowNumber: number;
  firefighterIdentifier: string | null;
  items: PreviewItem[];
  warnings: string[];
}

interface PreviewData {
  column_mappings: ColumnMapping[];
  unmatched_types: string[];
  total_firefighters: number;
  total_items: number;
  has_identifiers: boolean;
  preview: PreviewRow[];
  full_row_count: number;
}

interface CommitResult {
  created: number;
  skipped: number;
  errors: string[];
}

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length === 0) return { headers: [], rows: [] };

  // Simple CSV parser (handles basic cases, no quoted commas)
  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        inQuotes = !inQuotes;
      } else if (c === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Remove BOM if present
  const headerLine = lines[0].replace(/^\uFEFF/, '');
  const headers = parseLine(headerLine);
  const rows = lines.slice(1).map(l => parseLine(l));

  return { headers, rows };
}

export default function Import() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const [rawData, setRawData] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [result, setResult] = useState<CommitResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [identifierColumn, setIdentifierColumn] = useState<number | null>(null);

  const runPreview = async (data: { headers: string[]; rows: string[][] }, idColumn: number | null) => {
    setLoading(true);
    setError('');
    try {
      const previewData = await api.post<PreviewData>('/imports/firefighter-template/preview', {
        headers: data.headers,
        rows: data.rows,
        identifierColumn: idColumn,
      });
      setPreview(previewData);
      setStep('preview');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');

    const text = await file.text();
    const parsed = parseCSV(text);

    if (parsed.headers.length === 0) {
      setError('No data found in CSV file');
      return;
    }

    setRawData(parsed);

    // Auto-detect identifier column (look for name, email, badge, member columns)
    let detectedId: number | null = null;
    for (let i = 0; i < parsed.headers.length; i++) {
      const h = parsed.headers[i].toLowerCase();
      if (h.includes('name') || h.includes('email') || h.includes('badge') || h.includes('member') || h.includes('firefighter')) {
        detectedId = i;
        break;
      }
    }
    setIdentifierColumn(detectedId);

    await runPreview(parsed, detectedId);
  };

  const handleCommit = async () => {
    if (!rawData) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.post<CommitResult>('/imports/firefighter-template/commit', {
        headers: rawData.headers,
        rows: rawData.rows,
        identifierColumn,
      });
      setResult(data);
      setStep('done');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setRawData(null);
    setPreview(null);
    setResult(null);
    setError('');
    setIdentifierColumn(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 16px', color: '#111' }}>Import PPE Data</h1>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Upload Firefighter Template CSV</h2>

          <div style={{ padding: 12, background: '#f3f4f6', borderRadius: 8, marginBottom: 16, fontSize: 13, lineHeight: 1.6 }}>
            <strong>Supported format:</strong> One row per firefighter with date columns for each gear type.
            <br />
            Column headers like: "Structure Helmet - Date", "Frontline Structure Jacket - Date", "Backup Structure Pants - Date"
            <br />
            Dates in M/D/YYYY format. "Yes/No" backup columns are handled automatically.
            <br />
            Optionally include a Name, Email, or Badge column to auto-assign gear to users.
          </div>

          <div style={{
            border: '2px dashed #d1d5db', borderRadius: 12, padding: 40,
            textAlign: 'center', cursor: 'pointer', background: '#fafafa',
          }}
            onClick={() => fileRef.current?.click()}>
            <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect}
              style={{ display: 'none' }} />
            <div style={{ fontSize: 36, marginBottom: 8, color: '#9ca3af' }}>&#128196;</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: '#374151' }}>
              {loading ? 'Processing...' : 'Click to select CSV file'}
            </div>
            <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>or drag and drop</div>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && preview && (
        <div>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ background: 'white', borderRadius: 10, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#111' }}>{preview.total_firefighters}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Firefighters</div>
            </div>
            <div style={{ background: 'white', borderRadius: 10, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1d4ed8' }}>{preview.total_items}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Items to Create</div>
            </div>
            <div style={{ background: 'white', borderRadius: 10, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: preview.has_identifiers ? '#16a34a' : '#f59e0b' }}>
                {preview.has_identifiers ? 'Yes' : 'No'}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Identifier Found</div>
            </div>
          </div>

          {/* Column mappings */}
          <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>Column Mappings</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {preview.column_mappings.map((m, i) => (
                <span key={i} style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                  background: m.matched_item_type ? '#f0fdf4' : '#fef2f2',
                  color: m.matched_item_type ? '#16a34a' : '#dc2626',
                  border: `1px solid ${m.matched_item_type ? '#bbf7d0' : '#fecaca'}`,
                }}>
                  {m.column} → {m.gear_type}
                  {m.classification && <span style={{ marginLeft: 4, opacity: 0.7 }}>({m.classification})</span>}
                  {!m.matched_item_type && <span style={{ marginLeft: 4 }}>&#9888;</span>}
                </span>
              ))}
            </div>
            {preview.unmatched_types.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>
                Missing item types: {preview.unmatched_types.join(', ')}. Create them in Item Types first, or items of these types will be skipped.
              </div>
            )}
          </div>

          {/* Identifier column selector */}
          {rawData && (
            <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600 }}>Firefighter Identifier Column</h3>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                Select which column identifies the firefighter (to auto-assign gear). Leave as "None" if no identifier column exists.
              </p>
              <select value={identifierColumn ?? ''} onChange={e => {
                const val = e.target.value;
                const nextIdentifier = val === '' ? null : parseInt(val);
                setIdentifierColumn(nextIdentifier);
                if (rawData) void runPreview(rawData, nextIdentifier);
              }} style={{ ...inputStyle, maxWidth: 300, background: 'white' }}>
                <option value="">None (no auto-assignment)</option>
                {rawData.headers.map((h, i) => (
                  <option key={i} value={i}>{h}</option>
                ))}
              </select>
            </div>
          )}

          {/* Preview table */}
          <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                Preview (showing {preview.preview.length} of {preview.full_row_count} rows)
              </h3>
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Row</th>
                    {preview.has_identifiers && <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Firefighter</th>}
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Items</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map(row => (
                    <tr key={row.rowNumber} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{row.rowNumber}</td>
                      {preview.has_identifiers && (
                        <td style={{ padding: '10px 12px' }}>{row.firefighterIdentifier || '-'}</td>
                      )}
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {row.items.map((item, i) => (
                            <span key={i} style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                              background: item.classification === 'frontline' ? '#dbeafe' :
                                item.classification === 'backup' ? '#f3e8ff' : '#f3f4f6',
                              color: item.classification === 'frontline' ? '#1d4ed8' :
                                item.classification === 'backup' ? '#7c3aed' : '#374151',
                            }}>
                              {item.gearType}{item.instanceNumber > 1 ? ` #${item.instanceNumber}` : ''}
                              {item.classification && ` (${item.classification === 'frontline' ? 'FL' : 'BU'})`}
                              {item.dateManufactured && ` - ${item.dateManufactured}`}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={handleReset}
              style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>
              Cancel
            </button>
            <button onClick={handleCommit} disabled={loading || preview.total_items === 0}
              style={{
                padding: '10px 24px', background: '#b91c1c', color: 'white', border: 'none',
                borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
                opacity: loading || preview.total_items === 0 ? 0.7 : 1,
              }}>
              {loading ? 'Importing...' : `Import ${preview.total_items} Items`}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 'done' && result && (
        <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>Import Complete</h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div style={{ padding: 16, background: '#f0fdf4', borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#16a34a' }}>{result.created}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Items Created</div>
            </div>
            <div style={{ padding: 16, background: result.skipped > 0 ? '#fef2f2' : '#f3f4f6', borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: result.skipped > 0 ? '#dc2626' : '#6b7280' }}>{result.skipped}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Skipped</div>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div style={{ maxHeight: 200, overflow: 'auto', padding: 12, background: '#f9fafb', borderRadius: 8, marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>Errors</h3>
              {result.errors.map((err, i) => (
                <div key={i} style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{err}</div>
              ))}
            </div>
          )}

          <button onClick={handleReset}
            style={{ padding: '10px 20px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Import Another File
          </button>
        </div>
      )}
    </div>
  );
}
